import React, { useState, useEffect } from 'react';
import { galleryData } from '../../data/portfolioData';

const GallerySection = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [layout, setLayout] = useState("masonry"); // masonry or grid
  const [isLoading, setIsLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState({});
  
  // Simulate loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Extract all unique categories and tags
  const allCategories = ["All", ...Array.from(new Set(galleryData.map(item => item.category)))];
  const allTags = Array.from(new Set(galleryData.flatMap(item => item.tags)));
  
  // Filter gallery items based on category and search term
  const filteredGallery = galleryData.filter(item => {
    const matchesCategory = filter === "All" || item.category === filter;
    const matchesSearch = searchTerm === "" || 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      
    return matchesCategory && matchesSearch;
  });
  
  // Function to handle image load error
  const handleImageError = (id) => {
    console.error(`Failed to load image for item with ID: ${id}`);
    setImageErrors(prev => ({ ...prev, [id]: true }));
  };
  
  // Function to open the full image view
  const openLightbox = (imageId) => {
    setSelectedImage(imageId);
    document.body.style.overflow = "hidden"; // Prevent background scrolling
  };
  
  // Function to close the full image view
  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = "auto"; // Restore scrolling
  };
  
  // Get the selected image data
  const selectedImageData = selectedImage !== null 
    ? galleryData.find(item => item.id === selectedImage) 
    : null;
  
  // Navigate to the next or previous image in lightbox
  const navigateImage = (direction) => {
    if (!selectedImage) return;
    
    const currentIndex = filteredGallery.findIndex(item => item.id === selectedImage);
    const nextIndex = (currentIndex + direction + filteredGallery.length) % filteredGallery.length;
    
    setSelectedImage(filteredGallery[nextIndex].id);
  };
  
  // Handle keyboard navigation in lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") navigateImage(1);
      if (e.key === "ArrowLeft") navigateImage(-1);
    };
    
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage, filteredGallery]);
  
  return (
    <div className="py-16 relative">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-purple-500/5 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-20 -left-40 w-96 h-96 bg-blue-500/5 rounded-full filter blur-3xl"></div>
      </div>
      
      {/* Section header */}
      <div className="text-center mb-12 relative">
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">Through My Lens</h2>
        <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
          A collection of my favorite photographic moments capturing the beauty of the world around us
        </p>
      </div>
      
      {/* Filter controls */}
      <div className="max-w-7xl mx-auto mb-8 px-4 sm:px-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex overflow-x-auto pb-2 hide-scrollbar md:pb-0 md:flex-wrap gap-2">
            {allCategories.map(category => (
              <button
                key={category}
                onClick={() => setFilter(category)}
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap ${
                  filter === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
          
          <div className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search photos..."
              className="block w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg py-2 pl-10 pr-3 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => setLayout('masonry')}
              className={`p-2 rounded-lg ${
                layout === 'masonry' ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              aria-label="Masonry layout"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setLayout('grid')}
              className={`p-2 rounded-lg ${
                layout === 'grid' ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              aria-label="Grid layout"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      {/* Loading state */}
      {isLoading ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex justify-center py-20">
          <div className="flex flex-col items-center">
            <div className="relative w-12 h-12">
              <div className="absolute top-0 left-0 w-full h-full border-4 border-gray-200 dark:border-gray-700 rounded-full"></div>
              <div className="absolute top-0 left-0 w-full h-full border-4 border-transparent border-t-blue-500 rounded-full animate-spin"></div>
            </div>
            <p className="mt-4 text-gray-500 dark:text-gray-400">Loading gallery...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Gallery grid */}
          {filteredGallery.length > 0 ? (
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              {layout === 'masonry' ? (
                <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
                  {filteredGallery.map(item => (
                    <div 
                      key={item.id} 
                      className="relative overflow-hidden break-inside-avoid rounded-lg cursor-pointer transform transition-transform duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/10 mb-4"
                      onClick={() => openLightbox(item.id)}
                    >
                      <div className="aspect-auto bg-gray-200 dark:bg-gray-700 overflow-hidden h-48 md:h-64 lg:h-72">
                        {imageErrors[item.id] ? (
                          <div className="w-full h-full flex items-center justify-center text-gray-500 dark:text-gray-400 text-xl">
                            {item.title.charAt(0)}
                          </div>
                        ) : (
                          <img 
                            src={item.imageUrl}
                            alt={item.title}
                            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                            onError={() => handleImageError(item.id)}
                          />
                        )}
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                        <h3 className="text-white text-lg font-medium">{item.title}</h3>
                        <p className="text-gray-300 text-sm">{item.description}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {item.tags.map(tag => (
                            <span key={tag} className="text-xs bg-black/40 text-gray-200 px-2 py-1 rounded-full backdrop-blur-sm">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredGallery.map(item => (
                    <div 
                      key={item.id} 
                      className="group relative overflow-hidden rounded-lg cursor-pointer"
                      onClick={() => openLightbox(item.id)}
                    >
                      <div className="aspect-square overflow-hidden">
                        {imageErrors[item.id] ? (
                          <div className="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 text-2xl font-bold">
                            {item.title.charAt(0)}
                          </div>
                        ) : (
                          <img 
                            src={item.imageUrl}
                            alt={item.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            onError={() => handleImageError(item.id)}
                          />
                        )}
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                        <h3 className="text-white text-lg font-medium">{item.title}</h3>
                        <p className="text-gray-300 text-sm">{item.description}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {item.tags.map(tag => (
                            <span key={tag} className="text-xs bg-black/40 text-gray-200 px-2 py-1 rounded-full backdrop-blur-sm">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center py-16 border border-gray-200 dark:border-gray-700/50 rounded-lg">
              <svg className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="text-xl text-gray-600 dark:text-gray-400">No photos match your search</h3>
              <button 
                onClick={() => {setSearchTerm(""); setFilter("All");}}
                className="mt-4 text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300"
              >
                Clear filters
              </button>
            </div>
          )}
        </>
      )}
      
      {/* Lightbox */}
      {selectedImage !== null && selectedImageData && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center">
          <button 
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            aria-label="Close lightbox"
          >
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <button 
            onClick={() => navigateImage(-1)}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300"
            aria-label="Previous image"
          >
            <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <button 
            onClick={() => navigateImage(1)}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300"
            aria-label="Next image"
          >
            <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          
          <div className="max-w-5xl max-h-[80vh] w-full">
            <div className="relative">
              <div className="max-h-[70vh] h-[50vh] mx-auto overflow-hidden bg-gray-800 flex items-center justify-center">
                {imageErrors[selectedImageData.id] ? (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-4xl font-bold">
                    {selectedImageData.title.charAt(0)}
                  </div>
                ) : (
                  <img 
                    src={selectedImageData.imageUrl}
                    alt={selectedImageData.title}
                    className="max-h-full max-w-full object-contain"
                    onError={() => handleImageError(selectedImageData.id)}
                  />
                )}
              </div>
              
              <div className="mt-4 px-4 sm:px-0 text-white">
                <h3 className="text-2xl font-medium">{selectedImageData.title}</h3>
                <p className="text-gray-300 mb-2">{selectedImageData.description}</p>
                
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm mt-4">
                  <div>
                    <p className="text-gray-400">Location</p>
                    <p className="text-white">{selectedImageData.location}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Date</p>
                    <p className="text-white">{selectedImageData.date}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Camera</p>
                    <p className="text-white">{selectedImageData.camera}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Settings</p>
                    <p className="text-white">{selectedImageData.settings}</p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2 mt-4">
                  {selectedImageData.tags.map(tag => (
                    <span key={tag} className="text-xs bg-white/10 text-gray-200 px-3 py-1 rounded-full">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GallerySection;